/**
 * Beállítások — NAV-kapcsolat kezelése
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { get, post } from '../../lib/api';
import { PageHeader, Button, Card, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

export default function NavSettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isTester, setIsTester] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ navUser: '', navPassword: '', navSignatureKey: '', navExchangeKey: '' });

  useEffect(() => {
    (async () => {
      try {
        const data = await get<any>('/nav/credentials');
        setHasCredentials(!!(data.navLogin || data.nav_login));
        setIsTester(!!data.isTester);
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [key]: e.target.value }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await post('/setup/nav', form);
      toast('NAV-credentials mentve.', 'success');
      setHasCredentials(true);
      setForm({ navUser: '', navPassword: '', navSignatureKey: '', navExchangeKey: '' });
    } catch (err: any) {
      toast(err.message || 'Mentés sikertelen.', 'error');
    } finally { setSaving(false); }
  }

  const isValid = form.navUser.length >= 2 && form.navPassword.length >= 1 && form.navSignatureKey.length >= 1 && form.navExchangeKey.length >= 1;

  return (
    <div>
      <PageHeader title="NAV-kapcsolat" description="NAV Online Számla adatszolgáltatás beállítása" />

      <Card className="mb-4">
        <div className="card-h"><h3>Állapot</h3></div>
        <div className="row-li">
          <div className="ri-main">
            <div className="ri-t">NAV-credentials</div>
            <div className="ri-s">{loading ? 'Betöltés...' : hasCredentials ? 'Beállítva' : 'Nincs beállítva'}</div>
          </div>
          <div style={{ color: hasCredentials ? 'var(--green)' : 'var(--red)', fontWeight: 700, fontSize: '13px' }}>
            {hasCredentials ? '✓ Aktív' : '✗ Hiányzik'}
          </div>
        </div>
        <div className="row-li">
          <div className="ri-main">
            <div className="ri-t">Üzemmód</div>
            <div className="ri-s">Sandbox (teszt) — api-test.onlineszamla.nav.gov.hu</div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="card-h"><h3>{hasCredentials ? 'Credentials felülírása' : 'Credentials megadása'}</h3></div>

        {isTester && (
          <div style={{ background: '#fff8f0', border: '1px solid #ffd0a0', borderRadius: '8px', padding: '14px 18px', margin: '0 0 16px', fontSize: '14px', color: '#8b4513', lineHeight: 1.5 }}>
            🧪 <strong>Tesztelői fiók — SANDBOX mód</strong><br />
            Jelenleg egy SANDBOX (teszt) szerverhez van kapcsolva a NAV-összeköttetés, így bátran tesztelheted a számlázást — valódi adatok nem kerülnek az adóhivatalhoz. Saját NAV adatokat később itt tudsz megadni.
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4" style={{ padding: '4px 0' }}>
          <Input label="NAV technikai felhasználó *" value={form.navUser} onChange={set('navUser')} placeholder={isTester ? 'Tesztelői fiókban előre beállítva' : 'A NAV-ból kapott felhasználónév'} disabled={isTester} />
          <Input label="NAV jelszó *" type="password" value={form.navPassword} onChange={set('navPassword')} placeholder={isTester ? '••••••••' : 'A NAV-ból kapott jelszó'} disabled={isTester} />
          <Input label="Aláírási kulcs *" value={form.navSignatureKey} onChange={set('navSignatureKey')} placeholder={isTester ? 'Tesztelői fiókban előre beállítva' : 'A NAV-ból kapott aláírási kulcs'} disabled={isTester} />
          <Input label="Cserekulcs *" value={form.navExchangeKey} onChange={set('navExchangeKey')} placeholder={isTester ? 'Tesztelői fiókban előre beállítva' : 'A NAV-ból kapott cserekulcs'} disabled={isTester} />

          {!isTester && (
            <div className="onb-info">
              Ezeket az adatokat az <strong>onlineszamla.nav.gov.hu</strong> oldalon, a "Felhasználók → Új technikai felhasználó" menüben találod. Az adatok titkosítva tárolódnak.
            </div>
          )}

          {!isTester && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px' }}>
              <Button type="submit" disabled={!isValid} loading={saving}>Mentés</Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
